import { base32Decode } from '@commons/util/helper/base32';
import { i18n } from 'i18next';
import moment from 'moment-timezone';

import { MomentService, Option } from '@contact/upgrade';

import { MailComment } from '../mail-comment/models/mail-comment';
import { IEpticaExctractEvent, IEpticaExtractAttachmentAction, IEpticaExtractDescription } from '../models/eptica-extract';
import { EpticaSelector } from '../models/eptica-selector';

const base32Regex = /^[A-Z2-7]+=*$/;

export interface ExtracterDataWrapper {
  $i18next: i18n;
  requestId: number;
  baseUrl: string;
  local: string;
  moment: MomentService;
}

type Extracter = (sw: ExtracterDataWrapper, selector: EpticaSelector, node: Element) => IEpticaExctractEvent;
type DescExtracter = (sw: ExtracterDataWrapper, selector: EpticaSelector, node: Element) => IEpticaExtractDescription;

type Factory<T> = () => T;
type ExtracterFactory = Factory<Extracter>;
type DescExtracterFactory = Factory<DescExtracter>;

type ExtracterType = 'DEFAULT' | 'COMMENT' | 'ASK_FOR_HELP' | 'BOUNCE' | 'TRANSFER';
type DescExtracterType = 'SIMPLE' | 'MULTI';

type Spliters = (locale: string) => ISpliter;
interface ISpliter {
  start: string;
  end: string;
}

const DEFAULT_SPLITERS: Spliters = locale => {
  const spliters = {
    start: '',
    end: '',
  };
  switch (locale) {
    case 'en_US':
    case 'en_GB':
      spliters.start = ` by `;
      spliters.end = ` to `;
      break;
    case 'fr_Fr':
    default:
      spliters.start = ` par `;
      spliters.end = ` à `;
      break;
  }
  return spliters;
};

const MESSAGE_SPLITTERS: Spliters = locale => {
  const spliters = {
    start: '',
    end: '',
  };
  switch (locale) {
    case 'en_US':
    case 'en_GB':
      spliters.start = ` from `;
      break;
    case 'fr_Fr':
    default:
      spliters.start = ` de `;
      break;
  }
  return spliters;
};

const BOUNCE_SPLITTERS: Spliters = locale => {
  const spliters = {
    start: '',
    end: '',
  };
  switch (locale) {
    case 'en_US':
    case 'en_GB':
      spliters.start = ` by agent `;
      break;
    case 'fr_Fr':
    default:
      spliters.start = ` par l'agent `;
      break;
  }
  return spliters;
};

const DESC_EXTRACTERS: { [type: string]: DescExtracterFactory } = {
  [EpticaSelector.request_event_type_forwarded]: () => getDescriptionExtracter('MULTI'),
  [EpticaSelector.request_event_type_forwarded_with_attachments]: () => getDescriptionExtracter('MULTI'),
  [EpticaSelector.request_event_type_forwarded_by_agent_to_external_specialist]: () => getDescriptionExtracter('MULTI'),
  [EpticaSelector.request_event_type_forwarded_by_agent_to_group]: () => getDescriptionExtracter('MULTI'),
  [EpticaSelector.request_event_type_bounce_by_agent]: () => getDescriptionExtracter('SIMPLE', BOUNCE_SPLITTERS),
  [EpticaSelector.request_event_ask_for_help]: () => getDescriptionExtracter('MULTI'),
  [EpticaSelector.request_event_type_opening]: () => getDescriptionExtracter('SIMPLE', MESSAGE_SPLITTERS),
  [EpticaSelector.request_event_type_client_message]: () => getDescriptionExtracter('SIMPLE', MESSAGE_SPLITTERS),
  [EpticaSelector.request_event_type_response]: () => getDescriptionExtracter('SIMPLE', MESSAGE_SPLITTERS),
  [EpticaSelector.request_event_type_outgoing_mail]: () => getDescriptionExtracter('SIMPLE', MESSAGE_SPLITTERS),
  [EpticaSelector.request_event_type_monitoring]: () => getDescriptionExtracter('SIMPLE', MESSAGE_SPLITTERS),
  [EpticaSelector.request_event_type_followup]: () => getDescriptionExtracter('SIMPLE', MESSAGE_SPLITTERS),
  default: () => getDescriptionExtracter(),
};

export const EXTRACTERS: { [type: string]: ExtracterFactory } = {
  [EpticaSelector.request_event_type_forwarded]: () => getExtracter('TRANSFER'),
  [EpticaSelector.request_event_type_forwarded_with_attachments]: () => getExtracter('TRANSFER'),
  [EpticaSelector.request_event_type_forwarded_by_agent_to_external_specialist]: () => getExtracter('COMMENT'),
  [EpticaSelector.request_event_type_forwarded_by_agent_to_group]: () => getExtracter('TRANSFER'),
  [EpticaSelector.request_event_type_bounce_by_agent]: () => getExtracter('BOUNCE'),
  [EpticaSelector.request_event_type_bounce_by_supervisor]: () => getExtracter('COMMENT'),
  [EpticaSelector.request_event_type_closure]: () => getExtracter('COMMENT', true),
  [EpticaSelector.request_event_type_review_requested]: () => getExtracter('COMMENT', true),
  [EpticaSelector.request_event_type_approved_by_supervisor]: () => getExtracter('COMMENT', true),
  [EpticaSelector.request_event_type_rejected_by_supervisor]: () => getExtracter('COMMENT'),
  [EpticaSelector.request_event_ask_for_help]: () => getExtracter('ASK_FOR_HELP'),
  [EpticaSelector.request_event_type_opening]: () => getExtracter('DEFAULT', true),
  [EpticaSelector.request_event_type_client_message]: () => getExtracter('DEFAULT', true),
  [EpticaSelector.request_event_type_response]: () => getExtracter('DEFAULT', true),
  [EpticaSelector.request_event_type_outgoing_mail]: () => getExtracter('DEFAULT', true),
  [EpticaSelector.request_event_type_monitoring]: () => getExtracter('DEFAULT', true),
  [EpticaSelector.request_event_type_followup]: () => getExtracter('DEFAULT', true),
  default: () => getExtracter(),
};

// default eptica event extracter
// data extract : date, description, agent
const defaultExtracter: Extracter = (sw, selector, node) => {
  const date = selectInnerHtml(node, '.request_history_date p');
  const canal = selectInnerHtml(node, '.request_history_info_content_canal p span');
  const sender = selectInnerHtml(node, '.request_history_info_content_sender p span');
  const recipient = selectInnerHtml(node, '.request_history_info_content_recipient p span');
  const ccRecipient = selectInnerHtml(node, '.request_history_info_content_recipient_cc p span');
  const bccRecipient = selectInnerHtml(node, '.request_history_info_content_recipient_bcc p span');
  const subject = selectInnerHtml(node, '.request_history_info_content_messgae_subject p span');
  const content = selectInnerText(node, '.request_history_info_content_text .switchable_content_text p');
  const contentSrc = selectAttribute(node, '.switchable_content_html', 'data-src');
  // hasHtml for eptica >=9.6  , hasHtml === '' for eptica <9.6
  const hasHtml = selectAttribute(node, '.switchable_content .request_history_info_content_text', 'data-hasHtml');
  const contentId = selectAttribute(node, '[name=messageSelectionRequestHistory]', 'value');

  let dateFormatted: Date;
  switch (sw.local) {
    case 'en_US':
      dateFormatted = moment(date, 'MM/DD/YYYY HH:mm a').locale(sw.moment.getCurrentLanguage()).toDate();
      break;
    case 'en_GB':
    case 'fr_FR':
    default:
      dateFormatted = moment.tz(date, 'DD/MM/YYYY HH:mm', sw.moment.getServerTimeZone()).locale(sw.moment.getCurrentLanguage()).toDate();
      break;
  }

  // browse known agent extracter and select with given key
  const key = Object.keys(DESC_EXTRACTERS).filter(k => k === selector)[0];
  const descExtracter = getExtracterOrElse(key, DESC_EXTRACTERS);

  return {
    requestId: sw.requestId,
    selector,
    description: descExtracter(sw, selector, node),
    date: dateFormatted,
    attachment: [],
    canal,
    sender,
    recipient,
    ccRecipient,
    bccRecipient,
    subject: subject.trim(),
    contentId: contentId,
    content: content.trim(),
    contentSrc,
    contentHtml: '', // lazy loaded
    skill: {
      source: [],
      target: [],
    },
    isPlainTextFormat: hasHtml === 'false' || hasHtml === '' || !hasHtml,
  };
};

// comment eptica event extracter
// data extract : mail of recipient, mail subject treated, content mail message, comment agent
const commentExtracter: Extracter = (sw, selector, node) => {
  const evt = defaultExtracter(sw, selector, node);
  const comment = selectInnerHtml(node, '.request_history_info_content_user_comment .comment_content_text p');
  return {
    ...evt,
    comment,
  };
};

// comment eptica event extracter for ask for help eventd
// if answered event present we extract data
const askForHelpExtracter: Extracter = (sw, selector, node) => {
  const evt = withAttachmentExtracter(commentExtracter)(sw, selector, node);

  // ask for help answer is dom child of ask for help event
  // we can extract in this way because querySelector method select always first dom element
  ['.request_event_type_answer_ask_for_help', '.request_event_type_answer_ask_for_help_to_external_specialist']
    .map(childSelector => Option.of(node.querySelector(childSelector)))
    .filter(childNode => childNode.isDefined())
    .map(childNode => withAttachmentExtracter(commentExtracter)(sw, selector, childNode.get()))
    .forEach(responseEvt => (evt.response = responseEvt));

  return evt;
};

// comment eptica event extracter for transfer eventd
// if answered event present we extract data
const transferExtracter: Extracter = (sw, selector, node) => {
  const evt = withAttachmentExtracter(commentExtracter)(sw, selector, node);
  let comment = selectInnerHtml(node, '.request_history_info_content_user_comment .comment_content_text p');

  if (comment.length % 8 === 0 && base32Regex.exec(comment)) {
    const descriptionObject = base32Decode(comment, 'RFC4648');
    comment = String.fromCharCode.apply(String, new Uint8Array(descriptionObject));
  }

  [
    '.request_event_type_forwarded',
    '.request_event_type_forwarded_with_attachments',
    '.request_event_type_forwarded_by_agent_to_external_specialist',
    '.request_event_type_forwarded_by_agent_to_group',
  ]
    .map(childSelector => Option.of(node.querySelector(childSelector)))
    .filter(childNode => childNode.isDefined())
    .map(childNode => withAttachmentExtracter(commentExtracter)(sw, selector, childNode.get()))
    .forEach(responseEvt => (evt.response = responseEvt));

  try {
    const json = MailComment.deserialize(comment);

    evt.comment = json.comment || '';

    if (MailComment.isForwardToSkillType(json.type)) {
      evt.description.title = sw.$i18next.t('commons:history.mail.transfer_by_skill', { agent: json.agent });
      evt.description.shortTitle = sw.$i18next.t('commons:history.mail.short_title.request_event_type_forwarded_with_attachments');
      evt.skill = json.skill || json.data || { source: [], target: [] };
      evt.description.actor.to = (evt.skill.target && evt.skill.target.map(target => target.name)[0]) || '';
    } else if (MailComment.isForwardToTeamType(json.type)) {
      evt.description.title = sw.$i18next.t('commons:history.mail.transfer_by_team', {
        agent: json.agent,
        team: (json.extraData && json.extraData['target']) || '',
      });
      evt.description.shortTitle = sw.$i18next.t('commons:history.mail.short_title.request_event_type_forwarded_with_attachments');
      evt.description.actor.to = (json.data && json.data.target && json.data.target.map(target => target.name)[0]) || '';
    }
    evt.agent = json.agent || evt.description.actor.from;
  } catch (_ignore) {
    evt.comment = comment;
  }

  return evt;
};

// comment eptica event extracter for technical json comment
// content of comment is a json with IBounceComment format
const bounceExtracter: Extracter = (sw, selector, node) => {
  const evt = defaultExtracter(sw, selector, node);
  let comment = selectInnerHtml(node, '.request_history_info_content_user_comment .comment_content_text p');

  if (comment.length % 8 === 0 && base32Regex.exec(comment)) {
    const descriptionObject = base32Decode(comment, 'RFC4648');
    const descriptionString = String.fromCharCode.apply(String, new Uint8Array(descriptionObject));
    comment = descriptionString;
  }

  try {
    const json = MailComment.deserialize(comment);
    if (MailComment.isForwardToSkillType(json.type)) {
      evt.description.title = sw.$i18next.t('commons:history.mail.transfer_by_skill', { agent: json.agent });
      evt.description.shortTitle = sw.$i18next.t('commons:history.mail.short_title.request_event_type_forwarded_with_attachments');
      evt.skill = json.skill || json.data || { source: [], target: [] };
    } else if (MailComment.isForwardToTeamType(json.type)) {
      evt.description.title = sw.$i18next.t('commons:history.mail.transfer_by_team', { agent: json.agent, team: json.extraData['target'] });
      evt.description.shortTitle = sw.$i18next.t('commons:history.mail.short_title.request_event_type_forwarded_with_attachments');
    }
    evt.bounceType = json.type;
    evt.comment = json.comment || '';
    evt.agent = json.agent || evt.description.actor.from;
  } catch (_ignore) {
    evt.comment = comment;
  }
  return evt;
};

// force i18n unknow client name
function extractTitle(sw: ExtracterDataWrapper, selector: EpticaSelector, node: Element): string {
  const title = selectInnerHtml(node, '.request_history_agt_name p').trim();
  if (EpticaSelector.request_event_type_client_message === selector || EpticaSelector.request_event_type_opening === selector) {
    switch (sw.local) {
      case 'en_US':
      case 'en_GB':
        return title.replace('INCONNU INCONNU', 'UNKNOW UNKNOW');
      default:
        break;
    }
  }
  return title;
}

// with attachment eptica event extracter
// data extract : attachment array
function withAttachmentExtracter(extracter: Extracter): Extracter {
  return (sw, selector, node) => {
    const evt = extracter(sw, selector, node);
    // select first eptica_li to eliminate child container
    Option.of(node.querySelector('.eptica_li > .Request_open'))
      // select attachment dom in current child only
      .forEach(childNode =>
        Array.from(childNode.children)
          // filter element with class 'request_history_attachment'
          .filter(_childNode => _childNode.classList.contains('request_history_attachment'))
          // select all attachments detail
          .forEach(_childNode =>
            _childNode
              .querySelectorAll('.request_history_info_content_attachment_detail')
              // extract data attachment
              .forEach(attNode => {
                const name = selectInnerHtml(attNode, 'a');
                const href = selectAttribute(attNode, 'a', 'href');
                const url = `${sw.baseUrl}/${href.split('/').slice(2).join('/')}`;
                const attachment = {
                  title: name.trim(),
                  source: url,
                  filename: '',
                  file: {},
                  action: IEpticaExtractAttachmentAction.NONE,
                };
                evt.attachment = evt.attachment.concat(attachment);
              })
          )
      );
    return evt;
  };
}

// generic description extracter
// extract agent source and target from description eptica event
function getDescriptionExtracter(type: DescExtracterType = 'SIMPLE', spliters: Spliters = DEFAULT_SPLITERS): DescExtracter {
  return (sw, selector, node) => {
    const title = extractTitle(sw, selector, node);
    const spliter = spliters(sw.local);

    const description = {
      title: title,
      shortTitle: sw.$i18next.t('commons:history.mail.short_title.' + selector),
      actor: {
        from: '',
        to: '',
      },
    };

    const indexStart = title.indexOf(spliter.start);
    const indexTo = type === 'SIMPLE' ? title.length : title.indexOf(spliter.end);
    if (indexStart !== -1 && indexTo !== -1) {
      const start = indexStart + spliter.start.length;
      description.actor.from = title.substr(start, indexTo - start) || '';
    }
    if (type === 'MULTI' && indexTo !== -1) {
      const next = indexTo + spliter.end.length;
      description.actor.to = title.substr(next, title.length - next) || '';
    }
    return description;
  };
}

function getExtracter(type: ExtracterType = 'DEFAULT', withAttachment: boolean = false): Extracter {
  let extracter: Extracter;
  switch (type) {
    case 'COMMENT':
      extracter = commentExtracter;
      break;
    // askForHelp is always with attachment so return immediatly to avoid twice search
    case 'ASK_FOR_HELP':
      return askForHelpExtracter;
    case 'TRANSFER':
      return transferExtracter;
    case 'BOUNCE':
      extracter = bounceExtracter;
      break;
    case 'DEFAULT':
    default:
      extracter = defaultExtracter;
      break;
  }
  return withAttachment ? withAttachmentExtracter(extracter) : extracter;
}

function selectInnerHtml(node: Element, selector: string): string {
  return Option.of(node.querySelector(selector))
    .map(el => el.innerHTML || '')
    .getOrElse('');
}

function selectInnerText(node: Element, selector: string): string {
  return Option.of(node.querySelector(selector))
    .map(el => el.textContent || '')
    .getOrElse('');
}

function selectAttribute(node: Element, selector: string, attribute: string): string {
  return Option.of(node.querySelector(selector))
    .map(el => el.getAttribute(attribute) || '')
    .getOrElse('');
}

export function getExtracterOrElse<T>(key: string, extracters: { [type: string]: () => T }): T {
  // retrieve extracter
  const ex = Option.of(key)
    .flatMap(k => Option.of(extracters[k]))
    .getOrElse(extracters['default']);
  // new extracter instance
  return ex();
}
